/**
 * @file INCSUBSearchbar.js
 * @description A reusable debounced search bar component for filtering/searching data by source.
 * Supports dynamic behavior for different data sources like platforms, plans, pools, and users.
 * 
 */

import React, { useCallback, useState, useEffect } from 'react';
import './INCSUBSearchbar.css';
import { debounce } from 'lodash';
import { getPlatformList } from '../../../services/platformServices';
import { getPlanList } from '../../../services/planServices';
import { useDispatch } from 'react-redux';
import { setPlatformList } from '../../../features/platforms/platformsSlice';
import { setPlanList, setPlanListLoader } from '../../../features/plans/planSlice';

function INCSUBSearchbar({
  source,               // 'platforms' | 'plans' | 'pools' | 'user' | custom
  backgroundColor,      // Background color of the search input section
  placeholder,          // Placeholder text in the input
  searchFunction,       // Custom function to call on search (for 'user' or fallback)
  param,                // Additional parameters required for specific sources
  width = null,         // Optional width of the search bar
  height = null         // Optional height (currently unused)
}) {
  const [key, setKey] = useState(''); // Input state
  const dispatch = useDispatch();

  /**
   * Debounced function to avoid excessive calls during rapid typing.
   */
  const handleSearch = useCallback(
    debounce(async (searchTerm) => {
      if (source === 'platforms') {
        const list = await getPlatformList(searchTerm, param.pageNumber);
        dispatch(setPlatformList(list));
      } else if (source === 'plans') {
        dispatch(setPlanListLoader(true));
        const list = await getPlanList(searchTerm, param.platformId);
        if (list.status === 200) {
          dispatch(setPlanList(list.data.data.platformPlanDetailsListDto));
        }
        dispatch(setPlanListLoader(false));
      } else if (source === 'pools') {
        param.func(searchTerm); // Call provided pool handler
      } else if (source === 'user') {
        searchFunction(searchTerm); // Direct call with user-provided function
      } else {
        param.func(searchTerm); // Default fallback handler
      }
    }, 500),
    [dispatch, source, param, searchFunction]
  );

  // Cancel debounce on unmount
  useEffect(() => {
    return () => handleSearch.cancel();
  }, [handleSearch]);

  /**
   * Input change handler
   */
  const handleChange = (event) => {
    const value = event.target.value;
    setKey(value);
    handleSearch(value); // Debounced search
  };

  return (
    <div
      className='INCSUBSearchbar_search_section'
      style={{ backgroundColor: backgroundColor, width: width }}
    >
      <img src='/assets/icons/user/search.svg' alt='search_icon' />
      <input
        type='text'
        style={{ backgroundColor: backgroundColor }}
        placeholder={placeholder}
        value={key}
        onChange={handleChange}
      />
    </div>
  );
}

export default INCSUBSearchbar;
